// Grill kitchen ticket board: columns, move rules, the "Later" strip, timers, new-ticket chime
// detection and the option sold-out panel. Pure: no DOM, no Intl. The server is authoritative
// (KitchenTicket, 409 not_fired); this is display and local re-ticking only.

import { ticketClock } from '@popcart/core/v1';

/** @typedef {import('@popcart/core/v1').KitchenTicket} KitchenTicket */
/** @typedef {import('@popcart/core/v1').KitchenStatus} KitchenStatus */
/** @typedef {import('@popcart/core/v1').MenuItemAdmin} MenuItemAdmin */
/** @typedef {import('@popcart/core/v1').ProductLine} ProductLine */

/** A scheduled ticket can be started this long before its fire time (server: 409 not_fired). */
export const FIRE_LEAD_MS = 10 * 60_000;

/** @type {{ status: KitchenStatus; title: string }[]} */
export const COLUMNS = [
  { status: 'queued', title: 'Queued' },
  { status: 'on_grill', title: 'On the grill' },
  { status: 'packing', title: 'Packing' },
  { status: 'ready', title: 'Ready' },
];

/** @type {Record<KitchenStatus, KitchenStatus | null>} */
const NEXT = { queued: 'on_grill', on_grill: 'packing', packing: 'ready', ready: null };

/** The one step forward from here (null when Ready). @param {KitchenStatus} s */
export function nextStatus(s) {
  return NEXT[s] ?? null;
}

/** Undo is only on_grill → queued. @param {KitchenStatus} s */
export function canUndo(s) {
  return s === 'on_grill';
}

/** Allowed moves, mirroring the API: one step forward, or on_grill → queued. @param {KitchenStatus} from @param {KitchenStatus} to */
export function isAllowedMove(from, to) {
  return nextStatus(from) === to || (from === 'on_grill' && to === 'queued');
}

/** Button wording for the forward step. @param {KitchenStatus} s */
export function moveLabel(s) {
  if (s === 'queued') return 'Start grilling';
  if (s === 'on_grill') return 'Move to packing';
  if (s === 'packing') return 'Mark ready';
  return null;
}

/** @param {string | null | undefined} iso */
function ms(iso) {
  if (!iso) return NaN;
  return Date.parse(iso);
}

/**
 * A scheduled ticket that hasn't fired yet sits in the Later strip.
 * @param {KitchenTicket} t @param {number} now epoch ms
 */
export function isLater(t, now) {
  if (t.kitchenStatus !== 'queued') return false;
  const f = ms(t.fireAt);
  return Number.isFinite(f) && f > now;
}

/** Can this ticket be started now (≤ 10 min before fire_at)? @param {KitchenTicket} t @param {number} now */
export function canStart(t, now) {
  const f = ms(t.fireAt);
  if (!Number.isFinite(f)) return true;
  return now >= f - FIRE_LEAD_MS;
}

/**
 * Express first, then oldest placed, then code (stable for equal times).
 * @param {KitchenTicket} a @param {KitchenTicket} b
 */
function byUrgency(a, b) {
  if (a.speed !== b.speed) return a.speed === 'express' ? -1 : 1;
  if (a.placedAt !== b.placedAt) return a.placedAt < b.placedAt ? -1 : 1;
  return a.code < b.code ? -1 : a.code > b.code ? 1 : 0;
}

/**
 * Split tickets into the Later strip (by fire time) and the four columns (Express, then oldest).
 * @param {KitchenTicket[]} tickets @param {number} now
 * @returns {{ later: KitchenTicket[]; columns: Record<KitchenStatus, KitchenTicket[]> }}
 */
export function buildBoard(tickets, now) {
  /** @type {Record<KitchenStatus, KitchenTicket[]>} */
  const columns = { queued: [], on_grill: [], packing: [], ready: [] };
  /** @type {KitchenTicket[]} */
  const later = [];
  for (const t of tickets) {
    if (isLater(t, now)) later.push(t);
    else if (columns[t.kitchenStatus]) columns[t.kitchenStatus].push(t);
  }
  for (const c of COLUMNS) columns[c.status].sort(byUrgency);
  later.sort((a, b) => ms(a.fireAt) - ms(b.fireAt) || byUrgency(a, b));
  return { later, columns };
}

/** Reading order for keyboard selection: columns left to right, top to bottom. @param {ReturnType<typeof buildBoard>} board */
export function boardOrder(board) {
  return COLUMNS.flatMap((c) => board.columns[c.status]);
}

/**
 * Arrow-key selection across the board. Left/Right jump columns (keeping the row where possible),
 * Up/Down move within a column. Returns the new selected shopOrderId (or the current one).
 * @param {ReturnType<typeof buildBoard>} board
 * @param {string | null} currentId
 * @param {'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'ArrowDown'} key
 * @returns {string | null}
 */
export function moveSelection(board, currentId, key) {
  const cols = COLUMNS.map((c) => board.columns[c.status]);
  let ci = -1;
  let ri = -1;
  cols.forEach((col, i) => {
    const r = col.findIndex((t) => t.shopOrderId === currentId);
    if (r >= 0) {
      ci = i;
      ri = r;
    }
  });
  if (ci < 0) return boardOrder(board)[0]?.shopOrderId ?? null;
  const col = /** @type {KitchenTicket[]} */ (cols[ci]);
  if (key === 'ArrowUp') return col[Math.max(0, ri - 1)]?.shopOrderId ?? currentId;
  if (key === 'ArrowDown') return col[Math.min(col.length - 1, ri + 1)]?.shopOrderId ?? currentId;
  const dir = key === 'ArrowLeft' ? -1 : 1;
  for (let i = ci + dir; i >= 0 && i < cols.length; i += dir) {
    const c = /** @type {KitchenTicket[]} */ (cols[i]);
    if (c.length) return c[Math.min(ri, c.length - 1)]?.shopOrderId ?? currentId;
  }
  return currentId;
}

/**
 * The ticket's clock, re-ticked locally with the shared rule.
 * @param {Pick<KitchenTicket, 'readyBy'>} t @param {number} now
 */
export function clockFor(t, now) {
  return ticketClock({ now: new Date(now), readyBy: t.readyBy ? new Date(t.readyBy) : null });
}

/**
 * "12 min left", "Due now", "3 min late"; null when there is no ready-by time.
 * @param {{ minutesLeft: number | null; late: boolean }} c
 */
export function timerLabel(c) {
  if (c.minutesLeft === null) return null;
  if (c.late) return `${Math.abs(c.minutesLeft)} min late`;
  if (c.minutesLeft === 0) return 'Due now';
  return `${c.minutesLeft} min left`;
}

/** "6:40 PM" in local time (the shop's tablet is in Manila). @param {string | Date | null | undefined} iso */
export function clock12(iso) {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '—';
  const h = d.getHours();
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(d.getMinutes()).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

/** @param {KitchenTicket} t */
export function firesAtLabel(t) {
  return `Fires at ${clock12(t.fireAt)}`;
}

/** When the start button unlocks ("Can start at 6:30 PM"). @param {KitchenTicket} t */
export function startsAtLabel(t) {
  const f = ms(t.fireAt);
  if (!Number.isFinite(f)) return null;
  return `Can start at ${clock12(new Date(f - FIRE_LEAD_MS))}`;
}

/** "Fresh and Grocery items are packed elsewhere." or null. @param {ProductLine[]} lines */
export function otherLinesNote(lines) {
  const names = [...new Set(lines)].filter((l) => l !== 'grill').map((l) => (l === 'fresh' ? 'Fresh' : 'Grocery'));
  if (!names.length) return null;
  return `${names.join(' and ')} items on this order are packed elsewhere.`;
}

/** Put the server's updated ticket in place. @param {KitchenTicket[]} tickets @param {KitchenTicket} updated */
export function replaceTicket(tickets, updated) {
  let found = false;
  const out = tickets.map((t) => {
    if (t.shopOrderId !== updated.shopOrderId) return t;
    found = true;
    return updated;
  });
  return found ? out : [...out, updated];
}

/**
 * Plain-English problem for a failed move. `not_fired` names the unlock time.
 * @param {unknown} err @param {KitchenTicket} t
 */
export function moveProblem(err, t) {
  const e = /** @type {{ code?: string; status?: number; message?: string } | null} */ (err && typeof err === 'object' ? err : null);
  if (e?.code === 'not_fired') {
    const at = startsAtLabel(t);
    return `Too early to start. This order is scheduled; ${at ? at.charAt(0).toLowerCase() + at.slice(1) : 'try again closer to its fire time'}.`;
  }
  if (e?.code === 'conflict' || e?.status === 409) return 'Someone else moved this ticket. The board has been refreshed.';
  if (e?.code === 'network') return "Couldn't reach PopCart. Check the connection and try again.";
  return e?.message || "Couldn't move the ticket. Try again.";
}

/**
 * New-ticket chime. A ticket is "new" the first time it lands on the active board (not in Later):
 * either it just arrived, or a scheduled ticket just fired. Nothing chimes on the first load.
 * The Partner Hub layout already chimes for every newly *placed* order; to play the sound once,
 * a just-arrived ticket is skipped when the layout chimed it (layoutChimed) or is about to
 * (still placed and not yet in the layout's queue). Fired scheduled tickets always chime.
 *
 * @param {{ active: Set<string>; later: Set<string> } | null} prev
 * @param {KitchenTicket[]} tickets
 * @param {number} now
 * @param {{ queueIds?: Set<string>; layoutChimed?: Set<string> }} [layout]
 * @returns {{ chime: KitchenTicket[]; seen: { active: Set<string>; later: Set<string> } }}
 */
export function detectNewTickets(prev, tickets, now, layout = {}) {
  const active = new Set(prev?.active ?? []);
  const later = new Set(prev?.later ?? []);
  /** @type {KitchenTicket[]} */
  const chime = [];
  for (const t of tickets) {
    const id = t.shopOrderId;
    if (isLater(t, now)) {
      later.add(id);
      continue;
    }
    if (active.has(id)) continue;
    active.add(id);
    if (!prev) continue;
    if (prev.later.has(id)) {
      chime.push(t);
      continue;
    }
    if (layout.layoutChimed?.has(id)) continue;
    if (t.shopOrderStatus === 'placed' && !layout.queueIds?.has(id)) continue;
    chime.push(t);
  }
  return { chime, seen: { active, later } };
}

/** Toast text for chimed tickets. @param {KitchenTicket[]} chime */
export function chimeMessage(chime) {
  const first = chime[0];
  if (!first) return null;
  if (chime.length > 1) return `${chime.length} new kitchen tickets`;
  return `New ticket ${first.code}${first.speed === 'express' ? ' · Express' : ''}`;
}

/** Count of tickets the kitchen still has to cook or pack (badge). @param {KitchenTicket[]} tickets @param {number} now */
export function openCount(tickets, now) {
  return tickets.filter((t) => t.kitchenStatus !== 'ready' && !isLater(t, now)).length;
}

// ── Side panel: option sold-out toggles ─────────────────────────────────────────────────────

/**
 * Menu items with option groups, grouped by menu section (null section last, as "Other").
 * @param {MenuItemAdmin[]} items
 * @returns {{ section: string; items: MenuItemAdmin[] }[]}
 */
export function menuSections(items) {
  /** @type {Map<string, MenuItemAdmin[]>} */
  const m = new Map();
  for (const it of items) {
    if (!it.groups.some((g) => g.options.length)) continue;
    const key = it.menuSection ?? '';
    const list = m.get(key) ?? [];
    list.push(it);
    m.set(key, list);
  }
  const keys = [...m.keys()].sort((a, b) => (a === '' ? 1 : b === '' ? -1 : 0));
  return keys.map((k) => ({ section: k || 'Other', items: /** @type {MenuItemAdmin[]} */ (m.get(k)) }));
}

/** How many options are sold out right now. @param {MenuItemAdmin[]} items */
export function soldOutCount(items) {
  let n = 0;
  for (const it of items) for (const g of it.groups) for (const o of g.options) if (!o.isAvailable) n++;
  return n;
}

/**
 * Set one option's availability in the menu (optimistic update and rollback).
 * @param {MenuItemAdmin[]} items @param {string} optionId @param {boolean} isAvailable
 */
export function setOptionAvailability(items, optionId, isAvailable) {
  return items.map((it) => {
    if (!it.groups.some((g) => optionsOf(g).some((o) => o.id === optionId))) return it;
    return /** @type {MenuItemAdmin} */ ({
      ...it,
      groups: it.groups.map((g) => ({ ...g, options: optionsOf(g).map((o) => (o.id === optionId ? { ...o, isAvailable } : o)) })),
    });
  });
}

/** @typedef {import('@popcart/core/v1').ModifierOption & { id: string }} AdminOption */
/** The admin options of a group (with ids). @param {MenuItemAdmin['groups'][number]} g @returns {AdminOption[]} */
export function optionsOf(g) {
  return /** @type {AdminOption[]} */ (g.options);
}

/**
 * A group would have nothing left to pick for a required choice.
 * @param {MenuItemAdmin['groups'][number]} g
 */
export function requiredGroupEmpty(g) {
  return g.minChoices > 0 && g.options.every((o) => !o.isAvailable);
}
